/**
 * The Quick Play roster screen's model (slice 11): pure, no DOM. The roster itself (picks, both
 * depth charts, move sets, the bag) is `@tdmons/data/roster-draft`, shared with the harness since
 * slice 10a; this adds who it plays: a preset opponent (the kitted fixture teams; a
 * roster-building AI is a later slice, docs/decisions.md) and the AI level, and the words the
 * screen shows for a problem.
 *
 * Every function returns a new state; an edit the rules do not allow returns the state unchanged.
 */
import {
  emptyState as emptyDraft,
  problems as draftProblems,
  rosterTeam,
  teamDraft as draftTeam,
  type PackIndex,
  type RosterDraft,
} from '@tdmons/data/roster-draft';
import {
  balance,
  pickOpponent,
  type Difficulty,
  type RosterProblem,
  type Side,
  type Team,
  type TeamDraft,
  type TeamId,
} from '@tdmons/sim';
import { PRESETS, presetTeam, type PresetId } from '@tdmons/sim/fixtures/balanced-teams';

export {
  BAG_SIZE,
  ROSTER_SIZE,
  bagItemIds,
  combatant,
  indexPack,
  isPicked,
  learnable,
  positionsOf,
  recommend,
  recommendAll,
  removeMove,
  resetMoves,
  rolePosition,
  roster,
  setMove,
  slotOf,
  swap,
  teamName,
  toggleBag,
  togglePick,
  unpick,
  type PackIndex,
  type RosterPick,
  type SwapRef,
} from '@tdmons/data/roster-draft';
export type { Side };

export type OpponentId = PresetId;
export const OPPONENTS: readonly OpponentId[] = PRESETS;

/**
 * The levels the Match tab offers. All-Pro is hidden until the AI ladder holds (slice 10b: it plays
 * Pro about even, 50.5% at 1,000 games, so it is not yet a harder opponent; docs/slices.md); the Dev
 * tab and `?coach=allPro` still play it.
 */
export const MATCH_LEVELS: readonly Difficulty[] = ['rookie', 'pro'];

/**
 * The roster screen's state. The opponent is not in it: since slice 10c the AI picks its preset
 * once the player presses Play (`aiOpponent`), and a saved choice from before is ignored.
 */
export interface RosterState extends RosterDraft {
  level: Difficulty;
}

/**
 * A fresh screen: nobody picked, the recommended bag, the first preset (Team Electabuzz since
 * slice 10a; Team Raichu was re-picked to match it in slice 10b), Pro.
 */
export function emptyState(gens: readonly number[]): RosterState {
  return { ...emptyDraft(gens), level: 'pro' };
}

/** A kitted fixture team as the opponent, always team B. */
export const opponentTeam = (id: OpponentId): Team => presetTeam(id);

/**
 * The preset the AI fields against `player` (slice 10c): the one that stands better against it on
 * the matchup board (sim ai/pick.ts). No rng: the same roster always draws the same preset.
 */
export const aiOpponent = (player: Team): OpponentId =>
  pickOpponent(
    player,
    OPPONENTS.map((id) => ({ id, team: opponentTeam(id) })),
  ).id;

/** `?opponent=electabuzz|raichu`: a forced preset (debugging and screenshots), if it names one. */
export function opponentFromQuery(search: string): OpponentId | null {
  const v = new URLSearchParams(search).get('opponent');
  return OPPONENTS.find((id) => id === v) ?? null;
}

/** The player's team as the sim checks it, charts possibly not full yet. */
export function teamDraft(state: RosterState, pack: PackIndex): TeamDraft {
  return draftTeam(state, pack);
}

export function problems(state: RosterState, pack: PackIndex): RosterProblem[] {
  return draftProblems(state, pack);
}

/**
 * Both teams for `GameSession`, the opponent the AI's pick unless `force` names one; throws on a
 * team `problems` does not pass.
 */
export function toTeams(
  state: RosterState,
  pack: PackIndex,
  force?: OpponentId | null,
): Record<TeamId, Team> {
  const opponent = opponentTeam(force ?? aiOpponent(rosterTeam(state, pack)));
  return { A: rosterTeam(state, pack, opponent.name), B: opponent };
}

/** Which tab a problem shows on. */
export type Tab = 'team' | 'chart' | 'moves' | 'bag' | 'match';

export function problemTab(p: RosterProblem): Tab {
  switch (p.code) {
    case 'count':
    case 'species':
      return 'team';
    case 'empty-slot':
    case 'twice':
      return 'chart';
    case 'moves':
    case 'move-twice':
    case 'not-learned':
      return 'moves';
    default:
      return 'bag';
  }
}

const SIDE_NAME: Record<Side, string> = { offense: 'Offense', defense: 'Defense' };

/** A problem in words, for the inline list: "Pick 2 more (9 / 11)". */
export function problemText(p: RosterProblem, state: RosterState, pack: PackIndex): string {
  const name = (i: number) => {
    const pick = state.picks[i];
    return pick ? (pack.byId.get(pick.id)?.name ?? pick.id) : `#${i + 1}`;
  };
  const move = (id: string) => pack.moves.get(id)?.name ?? id;
  const item = (id: string) =>
    (balance.items as unknown as Record<string, { name?: string } | undefined>)[id]?.name ?? id;
  switch (p.code) {
    case 'count':
      return p.have < p.need
        ? `Pick ${p.need - p.have} more (${p.have} / ${p.need})`
        : `Too many: ${p.have} / ${p.need}`;
    case 'species':
      return `${name(p.index)} is picked twice`;
    case 'empty-slot':
      return `${SIDE_NAME[p.side]}: ${p.slot} is empty`;
    case 'twice':
      return `${SIDE_NAME[p.side]}: ${name(p.index)} is in two slots`;
    case 'moves':
      return p.count === 0
        ? `${name(p.index)} knows no moves`
        : `${name(p.index)} knows ${p.count} moves; 4 at most`;
    case 'move-twice':
      return `${name(p.index)} knows ${move(p.moveId)} twice`;
    case 'not-learned':
      return `${name(p.index)} cannot learn ${move(p.moveId)}`;
    case 'bag-size':
      return `Bag: ${p.have} items; ${p.max} at most`;
    case 'bag-item':
      return `Bag: ${item(p.item)} is not a bag item`;
    case 'item-clause':
      return `Bag: ${item(p.item)} twice`;
  }
}
